/**
 * newFeaturesBindings.js — Привязка новых функций v10 к UI
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 * Автоматически добавляет кнопки и обработчики для всех улучшенных модулей
 */

import { showToast } from "../utils/helpers.js";
import { log } from "../utils/logger.js";
import { getIcon, injectIconSprite } from "../core/iconManager.js";
import { getViewportIds, getActiveViewportId, find3DViewportId } from "../core/viewportState.js"; // [10.19.31]

// [10.19.31] Обёртки били в захардкоженное окно "view-axial", КОТОРОГО НЕ
// СУЩЕСТВУЕТ ни в одной раскладке (есть axial/sagittal/coronal/volume3d/
// view-3d) — «Фонарик», «Полосы», «Авто-экспозиция», «Экспорт GIF» и «Запись
// экрана» при живой привязке молча делали ничего. Теперь окно берётся живое:
const _окноАктивное = () => getActiveViewportId?.() || getViewportIds()[0] || null;
const _окно3D = () => find3DViewportId();

// Внедряем SVG-спрайт при загрузке
injectIconSprite();

// Ленивые импорты — загружаются только при первом использовании
const _import = {
  viewport4D:       () => import("../core/viewport4D.js"),
  viewportCamera:   () => import("../core/viewportCamera.js"),
  viewportCine:     () => import("../core/viewportCine.js"),
  viewportEffects:  () => import("../core/viewportEffects.js"),
  viewportKeyboard: () => import("../core/viewportKeyboard.js"),
  viewportPersistence: () => import("../core/viewportPersistence.js"),
  viewportScreenshot:  () => import("../core/viewportScreenshot.js"),
  viewportSync:     () => import("../core/viewportSync.js"),
  viewportUI:       () => import("../core/viewportUI.js"),
  viewportVoice:    () => import("../core/viewportVoice.js"),
  viewportAnalysis: () => import("../core/viewportAnalysis.js"),
  calciumScoreCore: () => import("../ai/calciumScoreCore.js"),
  vtkModule:        () => import("../compute/vtkModule.js"),
  gpuCompute:       () => import("../compute/gpuCompute.js"),
};

// ══════════════════════════════════════════════════════════════
//  ФУНКЦИИ ДЛЯ ВЫЗОВА ИЗ UI (безопасные обёртки с toast)
// ══════════════════════════════════════════════════════════════

// [10.19.31] УДАЛЕНЫ обёртки: computeCalciumScore, jumpToPathology,
// computeVolumeStats — их кнопки убраны из меню (решение 10.3.5 наконец
// исполнено, Агатстон один — вкладка ИИ); flyAlongBezier — полёт по четырём
// зашитым точкам мимо анатомии (настоящий облёт — Автовращение, 10.19.30);
// runGaussianBlur3D — сглаживала ВЫДУМАННЫЙ нулевой массив и хвалилась
// «завершено»; enable/disableVoiceControl — третий голосовой движок на той же
// кнопке (хозяин один — featureBindings); showRadialMenu — звала контейнер
// "viewport-axial", которого нет, и не имела ни одной кнопки-входа.

export async function computeEF() {
  try {
    const m = await _import.viewport4D();
    // [12.0.52] Здесь стояла вторая плашка «💓 ФВ: 58% (КДО…, КСО…)» —
    // поверх честного предупреждения самого расчёта «ЭТО НЕ ФРАКЦИЯ ВЫБРОСА
    // ЛЖ». Расчёт сам показывает результат с оговоркой; повторять его под
    // словом «ФВ» нельзя.
    await m.computeEF();
  } catch(e) { showToast("Отношение объёмов не посчитано: " + e.message, "error", 5000); log.warn("[newFeatures]", e.message); }
}

// [12.0.52] detectAorticAneurysm, autoDetectCriticalFindings, analyzeBrainAtrophy
// удалены вместе с кнопками «Скрининг аорты», «Критические находки», «Атрофия
// мозга» — почему, записано у самих функций в core/viewportAnalysis.js.

export async function segmentOrgansByDensity() {
  try {
    const m = await _import.viewportAnalysis();
    const result = await m.segmentOrgansByDensity();
    if (result?.organs) {
      const names = Object.keys(result.organs).join(", ");
      showToast(`🫁 Органы: ${names}`, "info", 3000);
    }
  } catch(e) { showToast("❌ Ошибка сегментации", "error"); log.warn("[newFeatures]", e.message); }
}

export async function exportAnimatedGIF() {
  // [12.0.52] Одна дорога с кнопкой GIF на панели кино — exportModule.exportAsGif.
  // Здесь был свой экспорт: всегда срезы 0…50 (то есть первые полсотни от края
  // серии, а не вокруг того, что на экране), а в окнах MPR кадры не менялись
  // вовсе — у объёмного окна нет setSliceIndex, и вызов молча пропускался:
  // GIF из 51 одинакового кадра.
  try {
    const { exportAsGif } = await import("./exportModule.js");
    await exportAsGif();
  } catch(e) { showToast("GIF не получился: " + e.message, "error", 5000); log.warn("[newFeatures]", e.message); }
}

/**
 * [12.0.52] Возвращает, НАЧАЛАСЬ ли запись. Прежде плашка «Запись экрана
 * начата» показывалась до попытки, а кнопка переворачивалась в «Стоп» даже
 * без окна или при отказе браузера (MediaRecorder без поддержки webm) —
 * и следующее нажатие «останавливало» несуществующую запись.
 * Пишется ВИДЕО ОКНА СНИМКА (холст), а не всего экрана.
 */
export function startScreenRecording() {
  const окно = _окноАктивное();
  if (!окно) { showToast("Запись: нет открытого окна", "warning", 2500); return Promise.resolve(false); }
  // Сам запуск записи в viewportScreenshot — синхронный и отвечает true/false.
  return _import.viewportScreenshot().then((m) => {
    const пошла = m.startScreenRecording(окно);
    if (!пошла) showToast("Запись не началась: окно без холста или браузер не пишет видео webm", "warning", 5000);
    return !!пошла;
  }).catch((e) => { showToast("Запись не началась: " + e.message, "error", 5000); return false; });
}

export async function stopScreenRecording() {
  try {
    const m = await _import.viewportScreenshot();
    const blob = await m.stopScreenRecording();
    if (blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `recording_${Date.now()}.webm`; a.click();
      URL.revokeObjectURL(url);
    }
  } catch(e) { log.warn("[newFeatures]", e.message); }
}





export function enableFlashlight() {
  const окно = _окноАктивное(); if (окно) import("../core/viewportEffects.js").then(m => m.enableFlashlight(окно));
}

export function disableFlashlight() {
  const окно = _окноАктивное(); if (окно) import("../core/viewportEffects.js").then(m => m.disableFlashlight(окно));
}

export function showLetterbox() {
  const окно = _окноАктивное(); if (окно) import("../core/viewportUI.js").then(m => m.showLetterbox(окно));
}

export function hideLetterbox() {
  const окно = _окноАктивное(); if (окно) import("../core/viewportUI.js").then(m => m.hideLetterbox(окно));
}


/**
 * [12.0.51] «Куб ориентации» — это библиотечный маркер ориентации Cornerstone
 * (OrientationMarkerTool, кнопка «Ориентация» на панели инструментов), а не
 * самодельный куб. Самодельный (core/viewportCamera.js, createOrientationCube)
 * был НЕВЕРЕН по-медицински: в системе пациента LPS передняя сторона — это −Y,
 * верхняя — +Z, а у него «A» висела на оси −Z, «S» на −Y (A/P перепутаны с
 * S/I). К тому же он рисовался один раз и не менялся при вращении (его
 * перерисовку никто не звал), грани выводились в обратном порядке глубины, а
 * второе нажатие ничего не делало — закрыть куб было нельзя. Теперь кнопка —
 * второй вход в тот же маркер: состояние одно, второе нажатие выключает.
 */
export async function createOrientationCube() {
  try {
    const м = await import('../tools/toolManager.js');
    const наПанели = document.querySelector('.tool-btn[data-tool="OrientationMarker"]');
    if (наПанели) наПанели.click();                    // хозяин: тост и подсветка — там
    else {
      м.setActiveTool?.('OrientationMarker');
      showToast(м.isDisplayToolEnabled?.('OrientationMarker') ? 'Маркер ориентации включён' : 'Маркер ориентации выключен', 'info', 1500);
    }
    document.getElementById('btn-orientation-cube')?.classList.toggle('active', !!м.isDisplayToolEnabled?.('OrientationMarker'));
  } catch (e) {
    log.warn('[newFeatures]', e.message);
    showToast('Маркер ориентации не включился: ' + e.message, 'warning', 4000);
  }
}

// [12.0.51] enableAutoExposure УДАЛЕНА вместе с кнопкой «Авто-экспозиция».
// Она звала m.enableAutoExposure у модуля viewportEffects, где такой функции
// нет, — TypeError уходил в log.warn, и кнопка не делала ничего (импорт
// динамический, link_check его не видит). Чинить вызов на viewportCamera
// было бы вредно: та функция раз в 0,5 с читает ОДИН пиксель в центре окна и
// сдвигает окно W/L (на объёме — передаточную функцию) на 5 %, пока пиксель
// не станет «средним», — бесконечно и без выключателя. Это переписывало бы
// окно, выбранное врачом, по случайной ткани в центре кадра.

/**
 * [11.1.1] Синхронизация окон: переключатель, который ОТЧИТЫВАЕТСЯ.
 *
 * Владелец сказал «кнопка мёртвая, в консоли даже нажатие не видно». Он был
 * прав по сути, а я — неправ, когда, прочитав код, ответил «работает».
 * Код действительно исполнялся, но наблюдать это было НЕЧЕМ:
 *
 *   • об успехе не сообщалось — только log.debug, невидимый при обычном уровне;
 *   • об отказе не сообщалось вовсе: `if (!re) return`, и тишина;
 *   • при одном окне в раскладке связывать нечего, и молчание было
 *     неотличимо от поломки;
 *   • сбой внутри уходил в необработанный промис — `.then()` без `.catch()`;
 *   • выключить связку было нечем: функции disable* есть, но их никто не звал.
 *
 * Урок общий: «код исполняется» и «кнопка работает» — разные утверждения.
 * Вторая требует, чтобы человек увидел результат.
 */
// [11.1.1] Имя обёртки РАЗВЕДЕНО с ядром. Пока обе звались syncAllViewports,
// одна была синхронной (ядро, возвращает сводку), другая асинхронной (эта,
// переключатель с отчётом) — и это поймал await_test, увидев вызов ядра без
// await внутри асинхронной тёзки. Две разные функции под одним именем — ровно
// та ловушка, которую мы весь месяц выкорчёвываем.
/**
 * [12.0.57] ЧТО именно связано — словами. До 12.0.57 связка не срабатывала
 * вовсе (слушала не там — core/viewportSync._слушать), а тост говорил «Окна
 * связаны: 3 MPR». Теперь у трёх плоскостей MPR общее окно W/L, а вместе
 * листаются только окна одной ориентации — так и сказано.
 *
 * «Развязать» снимает и связку объёмных окон (unsync3DCameras прежде не
 * звали — камеры двух 3D-окон оставались связанными) и возвращает обычное
 * состояние MPR: общее окно W/L реформатов одной серии (uiManager, [MPR-SYNC]).
 */
function _чтоСвязано(и) {
  const мпр = и?.mpr ?? 0, стопок = и?.stack ?? 0, объёмных = и?.vol3d ?? 0, листаются = и?.mprЛистание ?? 0;
  const части = [];
  if (мпр) части.push(листаются ? `окна реформата (${мпр}): окно W/L общее, вместе листаются окна одной ориентации (${листаются})` : `окна реформата (${мпр}): окно W/L общее, а плоскости разные — каждая листается своя`);
  if (стопок) части.push(`стопки (${стопок}): листаются вместе — по глубине среза, если серии в одной системе координат, иначе по доле серии`);
  if (объёмных) части.push(`объёмные окна (${объёмных}): поворот общий`);
  return части;
}

export async function переключитьСвязкуОкон() {
  const кн = document.getElementById('btn-sync-all');
  const включено = кн?.dataset?.on === 'true';
  try {
    const m = await import('../core/viewportSync.js');
    if (включено) {
      m.disableMPRSync?.(); m.disablePositionSync?.(); m.disableStackScrollSync?.(); m.unsync3DCameras?.();
      m.enableMPRSync?.();   // [12.0.57] обычное состояние MPR — см. выше
      if (кн) { кн.dataset.on = 'false'; кн.classList.remove('active'); }
      showToast('Связка окон выключена', 'info', 2000);
      return;
    }
    const и = m.syncAllViewports();
    // Отчитываемся ЧИСЛАМИ: сколько окон связано и каких. Если связывать
    // нечего — говорим это прямо, а не молчим.
    const части = _чтоСвязано(и);
    if (!части.length) {
      showToast('Связывать нечего: нужно минимум два окна одного вида. Смените раскладку на MPR или 2×2', 'warning', 5000);
      return;
    }
    if (кн) { кн.dataset.on = 'true'; кн.classList.add('active'); }
    showToast(`Окна связаны — ${части.join('; ')}. Нажмите ещё раз, чтобы развязать`, 'success', 6000);
  } catch (e) {
    // Раньше это исключение уходило в необработанный промис — на экране
    // не появлялось ничего, и кнопка выглядела мёртвой.
    log.warn('[Синхронизация]', e.message);
    showToast('Связать окна не удалось: ' + e.message, 'error', 4000);
  }
}

// [12.0.51] Тур записывался, но проиграть его было нечем: stopRecording
// возвращал кадры камеры, а их выбрасывали после тоста «Тур записан: N
// кадров». playbackRecording в ядре был, но его не звал никто. Теперь
// последний тур хранится и проигрывается кнопкой «Повторить тур».
let _последнийТур = null;

export async function startTourRecording() {
  try {
    const m = await _import.viewportCamera();
    const о3 = _окно3D();
    if (!о3) { showToast("Тур: нет объёмного окна — откройте MPR 6 или 3D", "warning", 3000); return false; }
    m.startRecording(о3, 10);
    showToast("🔴 Запись тура: вращайте и приближайте объём мышью, затем «Стоп тур»", "info", 3500);
    return true;
  } catch(e) { log.warn("[newFeatures]", e.message); showToast("Запись тура не началась: " + e.message, "warning", 4000); }
  return false;
}

export async function stopTourRecording() {
  try {
    const m = await _import.viewportCamera();
    const tour = m.stopRecording();
    const кадров = tour?.keyframes?.length || 0;
    if (кадров < 2) {
      showToast("Тур пуст: за время записи камера не двигалась", "warning", 3500);
      return;
    }
    _последнийТур = tour;
    showToast(`⏹ Тур записан: ${кадров} кадров, ${(tour.duration / 1000).toFixed(1)} с. «Повторить тур» проиграет его`, "success", 3500);
  } catch(e) { log.warn("[newFeatures]", e.message); }
}

/** Проиграть последний записанный тур в текущем объёмном окне. */
export async function playTour() {
  if (!_последнийТур?.keyframes?.length) { showToast("Сначала запишите тур: «Запись тура», движение мышью, «Стоп тур»", "info", 3500); return; }
  const о3 = _окно3D();
  if (!о3) { showToast("Тур: нет объёмного окна — откройте MPR 6 или 3D", "warning", 3000); return; }
  try {
    const m = await _import.viewportCamera();
    m.playbackRecording(о3, _последнийТур);
    showToast(`▶ Тур: ${(_последнийТур.duration / 1000).toFixed(1)} с`, "info", 2000);
  } catch (e) {
    log.warn("[newFeatures]", e.message);
    showToast("Тур не проигрался: " + e.message, "warning", 4000);
  }
}

export async function enableRRTimeline() {
  try {
    const m = await _import.viewportCine();
    const container = document.getElementById("rr-timeline-container");
    if (!container) {
      const div = document.createElement("div");
      div.id = "rr-timeline-container";
      div.style.cssText = "position:fixed; bottom:20px; left:50%; transform:translateX(-50%); z-index:1000;";
      document.body.appendChild(div);
      const окно = _окноАктивное(); if (окно) m.createRRTimeline(окно, "rr-timeline-container");
    }
  } catch(e) { log.warn("[newFeatures]", e.message); }
}

/**
 * [11.1.1] Все жесты — с отчётом и честным предупреждением.
 *
 * Владелец спросил: «эта кнопка вообще непонятно что делает». Справедливо —
 * она молчала, а название не объясняло. По коду (viewportUI.enableGestures)
 * это СЕНСОРНЫЕ жесты: двойное касание — сброс, щипок — масштаб, свайп —
 * листание. На компьютере с мышью они не срабатывают вовсе, потому что
 * слушают touchstart/touchmove.
 *
 * Поэтому: считаем окна, говорим сколько включено, и на устройстве без сенсора
 * прямо предупреждаем, что толку не будет. Молчать здесь — обманывать.
 */
export async function enableAllGestures() {
  try {
    const m = await import('../core/viewportUI.js');
    m.enableAllGestures();
    const окон = (typeof getViewportIds === 'function' ? getViewportIds() : []).length;
    const естьСенсор = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    if (!окон) { showToast('Жесты включать не на чем: нет открытых окон', 'warning', 3000); return; }
    showToast(естьСенсор
      ? `Сенсорные жесты включены в ${окон} окнах: двойное касание — сброс, щипок — масштаб, свайп — листание`
      : `Жесты включены в ${окон} окнах, но экран не сенсорный — они не сработают. Пользуйтесь мышью и колесом`,
    естьСенсор ? 'success' : 'warning', естьСенсор ? 3500 : 5000);
  } catch (e) {
    log.warn('[Жесты]', e.message);
    showToast('Жесты включить не удалось: ' + e.message, 'error', 4000);
  }
}

export async function startMacroRecording() {
  try {
    const m = await _import.viewportKeyboard();
    m.startMacroRecording();
  } catch(e) { log.warn("[newFeatures]", e.message); }
}

export async function stopMacroRecording() {
  try {
    const m = await _import.viewportKeyboard();
    const macro = m.stopMacroRecording();
    if (macro) showToast(`⏹ Макрос: ${macro.steps.length} шагов`, "success", 2000);
  } catch(e) { log.warn("[newFeatures]", e.message); }
}

// Экспорт для глобального доступа
if (typeof window !== "undefined") {
  window.__newFeatures = {
    computeEF, segmentOrgansByDensity, exportAnimatedGIF,
    startScreenRecording, stopScreenRecording, enableFlashlight,
    disableFlashlight, showLetterbox, hideLetterbox, createOrientationCube,
    переключитьСвязкуОкон, startTourRecording,
    stopTourRecording, playTour, enableRRTimeline, enableAllGestures,
    startMacroRecording, stopMacroRecording,
  };
}

log.debug("[newFeatures] Bindings loaded — 30+ functions available");

// ══════════════════════════════════════════════════════════════
//  ПРИВЯЗКИ КНОПОК v10 — ПЕРЕЕХАЛИ СЮДА ИЗ index.html  [11.0.9]
// ══════════════════════════════════════════════════════════════
/**
 * ПОВОД. Эти девять привязок жили инлайн-скриптом в конце index.html. Работали
 * они исправно, но были НЕВИДИМЫ для обычного поиска: кто искал обработчик в
 * src/ и не находил, делал вывод «кнопка мёртвая».
 *
 * Ловушка не выдуманная — в неё попадал я сам при разборе верхнего меню:
 * поиск показал «привязки не найдено» у «Фракции выброса» и «Атрофии мозга»,
 * хотя обе живые. В enterprisePanel описан тот же случай словами: «пока я
 * смотрел только src/, три живые кнопки выглядели мёртвыми, и я чуть не
 * оставил их дубли».
 *
 * Цена ошибки здесь — удалённая клиническая функция. Поэтому привязки
 * перенесены в модуль, где их видят и поиск, и сторожа (uiwiring_test читает
 * src/ целиком).
 *
 * Поведение сохранено дословно, включая переключатели «запись/стоп» и
 * «включено/выключено» на dataset. Единственное добавление — сигнал прерывания,
 * чтобы слушателей можно было снять при разборке (в инлайне этого не было).
 */
let _acНовые = null;
function _сигнал() {
  if (!_acНовые) _acНовые = new AbortController();
  return _acНовые.signal;
}

/** Кнопка-переключатель на dataset: одно нажатие включает, второе выключает. */
function _переключатель(ид, поле, включить, выключить, текстВкл, текстВыкл) {
  const кн = document.getElementById(ид);
  if (!кн) return;
  кн.addEventListener('click', () => {
    const было = кн.dataset[поле] === 'true';
    if (было) { выключить(); кн.dataset[поле] = 'false'; if (текстВыкл) кн.textContent = текстВыкл; }
    else { включить(); кн.dataset[поле] = 'true'; if (текстВкл) кн.textContent = текстВкл; }
    // Подпись меняем ТОЛЬКО когда она задана: у «Фонарика» и «Полос» в кнопке
    // лежит значок, и запись текста стёрла бы его — в инлайне подпись этим
    // двум и не трогали.
  }, { signal: _сигнал() });
}

export function initNewFeaturesButtons() {
  const кн = (ид, fn) => document.getElementById(ид)?.addEventListener('click', fn, { signal: _сигнал() });

  // ── Клинические расчёты (меню «Анализ») ──
  кн('btn-compute-ef', computeEF);
  // [12.0.52] btn-aortic-aneurysm, btn-brain-atrophy, btn-auto-detection — удалены.

  // ── Экспорт ──
  кн('btn-export-gif', exportAnimatedGIF);
  // [12.0.52] Переключатель — по НАСТОЯЩЕМУ состоянию записи (как у «Записи
  // тура» в 12.0.51): «Стоп» — только если запись пошла; подпись меняется
  // через innerHTML-копию, значок кнопки не стирается.
  {
    const кнЗ = document.getElementById('btn-screen-record');
    const исходно = кнЗ?.innerHTML;
    кн('btn-screen-record', async () => {
      if (кнЗ?.dataset.recording === 'true') {
        await stopScreenRecording();
        if (кнЗ) { кнЗ.dataset.recording = 'false'; кнЗ.innerHTML = исходно; кнЗ.classList.remove('active'); }
      } else if (await startScreenRecording()) {
        if (кнЗ) { кнЗ.dataset.recording = 'true'; кнЗ.innerHTML = '<svg class="ico" aria-hidden="true"><use href="#i-rect"/></svg> Стоп запись'; кнЗ.classList.add('active'); }
      }
    });
  }

  // ── 3D: камера и эффекты ──
  кн('btn-orientation-cube', createOrientationCube);
  // [12.0.51] Тур — свой переключатель по НАСТОЯЩЕМУ состоянию записи. Общий
  // _переключатель переворачивал кнопку в «Стоп тур», даже когда запись не
  // началась (нет 3D-окна), и стирал значок кнопки текстом.
  {
    const кнТур = document.getElementById('btn-tour-record');
    const исходно = кнТур?.innerHTML;
    кн('btn-tour-record', async () => {
      const m = await _import.viewportCamera();
      if (m.isRecording()) {
        await stopTourRecording();
        if (кнТур) { кнТур.innerHTML = исходно; кнТур.classList.remove('active'); }
      } else if (await startTourRecording()) {
        if (кнТур) { кнТур.innerHTML = '<svg class="ico" aria-hidden="true"><use href="#i-rect"/></svg> Стоп тур'; кнТур.classList.add('active'); }
      }
    });
  }
  _переключатель('btn-flashlight', 'on', enableFlashlight, disableFlashlight, null, null);
  _переключатель('btn-letterbox', 'on', showLetterbox, hideLetterbox, null, null);
  кн('btn-tour-play', playTour);   // [12.0.51]
  // [12.0.51] btn-auto-exposure удалена — надгробие у createOrientationCube выше.

  // [10.19.31] Привязки голоса здесь НЕТ и не должно быть: у btn-voice-control
  // было три хозяина — этот блок, appInit и featureBindings, — три
  // распознавателя на один клик. Единственный хозяин — featureBindings.

  кн('btn-gestures-all', enableAllGestures);
  кн('btn-sync-all', переключитьСвязкуОкон);
  // [12.0.57] Смена раскладки пересоздаёт окна: связка, сделанная для старых,
  // к новым не относится. Кнопка не должна гореть «связано» над окнами,
  // которых никто не связывал.
  import('../core/eventBus.js').then(({ on, EVENTS }) => on(EVENTS.LAYOUT_CHANGED, () => {
    const к = document.getElementById('btn-sync-all');
    if (к?.dataset?.on !== 'true') return;
    import('../core/viewportSync.js').then((m) => { m.disablePositionSync?.(); m.disableStackScrollSync?.(); m.unsync3DCameras?.(); }).catch(() => {});
    к.dataset.on = 'false'; к.classList.remove('active');
    showToast('Раскладка сменилась — связка окон снята; нажмите «Связать окна» снова, если нужно', 'info', 3500);
  })).catch((e) => log.debug('[Связка] шина:', e.message));

  log.debug('[newFeatures] привязки кнопок v10 подключены из модуля');
}

/** Снять слушателей — для разборки интерфейса. */
export function destroyNewFeaturesButtons() {
  if (_acНовые) { _acНовые.abort(); _acНовые = null; }
}
